import { Flex, Text, Box, FlexProps } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import { CloseCircleIcon } from "../icons/CloseIcon";
import { GradientBorderContainer } from "../containers/GradientBorderContainer";
import { Z_INDEX_READER_BANNER } from "../../screens/root/constants";

interface RoundedFooterBannerProps {
  text: string | React.ReactNode;
  actions?: React.ReactNode;
  containerProps?: FlexProps;
  onDismiss?: () => void;
  isDismissed?: boolean;
}

export function RoundedFooterBanner({
  text,
  actions,
  containerProps,
  isDismissed = false,
  onDismiss,
}: RoundedFooterBannerProps) {
  const isMobile = useMobileBreakpoint();
  // Animation variants
  const variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: "easeInOut" as const },
    },
    exit: {
      opacity: 0,
      y: 40,
      transition: { duration: 0.35, ease: "easeInOut" as const },
    },
  };
  // Ensure zIndex is a number for the style prop
  let zIndex = Z_INDEX_READER_BANNER;
  if (containerProps?.zIndex && typeof containerProps.zIndex === "number") {
    zIndex = containerProps.zIndex;
  }

  return (
    <AnimatePresence>
      {!isDismissed && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={variants}
          style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex }}
        >
          <GradientBorderContainer
            topColor="rgba(255, 255, 255, 0.3)"
            bottomColor="rgba(255, 255, 255, 0.06)"
            background="linear-gradient(327.76deg, rgba(20, 21, 23, 0.9) 64.1%, rgba(255, 90, 44, 0.9) 314.68%)"
            backgroundColor="charcoal.charcoal"
            padding={isMobile ? "16px" : "24px 40px"}
            backdropFilter="blur(24px)"
            boxShadow="0px 4px 24px 0px rgba(0, 0, 0, 0.40)"
            borderTopRadius="16px"
            overflow="visible"
            justifyContent="center"
            {...containerProps}
          >
            <Box position="absolute" top="16px" right="16px" zIndex={3}>
              <CloseCircleIcon
                onClick={onDismiss}
                color="dune.100"
                style={{ cursor: "pointer" }}
                boxSize="24px"
              />
            </Box>
            <Flex
              position="relative"
              zIndex={2}
              padding="12px"
              flexDir={isMobile ? "column" : "row"}
              gap={isMobile ? "12px" : "24px"}
              justifyContent="center"
              alignItems="center"
            >
              <Text
                fontSize={isMobile ? "12px" : "14px"}
                lineHeight="150%"
                color="dune.700"
                textAlign="center"
              >
                {text}
              </Text>
              {!isMobile && (
                <Box width="1px" height="32px" bg="transparent.white.20" />
              )}
              <Flex flexDir="row" gap="8px">
                {actions}
              </Flex>
            </Flex>
          </GradientBorderContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
